/**
 * Plugin Bridge
 *
 * Handles communication between the host application and plugin iframes.
 */

import type {
  AnyPluginMessage,
  PluginApiCallMessage,
  PluginApiResponseMessage,
  PluginEventMessage,
  PluginPropsUpdateMessage,
} from './sdk/bridge-protocol';
import { isValidPluginMessage } from './sdk/bridge-protocol';
import client from '../api/client';
import { getPluginSettings, updatePluginSettings } from '../api/plugins';
import { pluginToastBridge } from './toast-bridge';
import { pluginModalBridge } from './modal-bridge';
import {
  startFrontendSpan,
  endFrontendSpan,
} from '../utils/observability-service';
import type { CognitivePhase } from '../types/observability';
import { usePluginEventBusStore } from './plugin-event-bus';

interface PluginBridgeConfig {
  pluginId: string;
  iframe: HTMLIFrameElement;
  allowedOrigin: string;
  onReady?: () => void;
  onError?: (error: string) => void;
  onResize?: (width: number, height: number) => void;
  /** Wave 17: Enable DevTools integration for this plugin iframe */
  devtools?: boolean;
  /** Wave 17: Callback when a plugin emits a span */
  onPluginSpan?: (span: { spanId: string; operationName: string; status: string; durationMs?: number; attributes?: Record<string, unknown> }) => void;
  /** Wave 17: Callback when a plugin emits a log */
  onPluginLog?: (log: { level: string; message: string; timestamp: string; attributes?: Record<string, unknown> }) => void;
  /** Wave 17: Callback when a plugin emits an error */
  onPluginError?: (error: { message: string; category?: string; code?: string; timestamp: string }) => void;
  /** Wave 18c: Callback when plugin saves state snapshot before reload */
  onStateSnapshot?: (state: Record<string, unknown>) => void;
  /** Wave 18c: Callback when plugin requests a hot reload retry */
  onReloadRetry?: () => void;
  /** Wave 18c: Callback when plugin requests to open DevTools */
  onOpenDevTools?: () => void;
}

type ApiHandler = (args: any[]) => Promise<any>;

/**
 * Host-side plugin bridge
 */
export class PluginBridge {
  private readonly pluginId: string;
  private readonly iframe: HTMLIFrameElement;
  private readonly allowedOrigin: string;
  private readonly onReady?: () => void;
  private readonly onError?: (error: string) => void;
  private readonly onResize?: (width: number, height: number) => void;
  private readonly apiHandlers: Map<string, ApiHandler>;
  private readonly eventSubscriptions: Map<string, Set<(payload: any) => void>>;
  private readonly pendingResponses: Map<string, (response: PluginApiResponseMessage) => void>;
  /** Wave 17: Full config reference for DevTools forwarding */
  private readonly config: PluginBridgeConfig;
  private ready = false;
  private destroyed = false;
  // Wave 16: observability context
  private observabilityContext: {
    traceId?: string;
    spanId?: string;
    correlationId?: string;
    currentPhase?: CognitivePhase;
  } = {};
  // Wave 18c: saved plugin state for hot reload
  private savedPluginState: Record<string, unknown> | null = null;
  /** Plugin span id → the host span started for it (startFrontendSpan makes its own id). */
  private readonly pluginSpans = new Map<string, string>();

  constructor(config: PluginBridgeConfig) {
    this.config = config;
    this.pluginId = config.pluginId;
    this.iframe = config.iframe;
    this.allowedOrigin = config.allowedOrigin;
    this.onReady = config.onReady;
    this.onError = config.onError;
    this.onResize = config.onResize;
    this.apiHandlers = new Map();
    this.eventSubscriptions = new Map();
    this.pendingResponses = new Map();

    this.registerDefaultHandlers();
    this.setupMessageListener();
  }

  /**
   * Check if bridge is ready
   */
  isReady(): boolean {
    return this.ready && !this.destroyed;
  }

  /**
   * Send a message to the plugin
   */
  sendMessage(message: AnyPluginMessage): void {
    if (this.destroyed) return;
    if (!this.iframe.contentWindow) return;

    this.iframe.contentWindow.postMessage(
      { ...message, pluginId: this.pluginId },
      this.allowedOrigin,
    );
  }

  /**
   * Send an event to the plugin
   */
  sendEvent(eventType: string, payload: any): void {
    const message: PluginEventMessage = {
      type: 'event',
      pluginId: this.pluginId,
      event: {
        type: eventType,
        payload,
        timestamp: Date.now(),
      },
    };
    this.sendMessage(message);
  }

  /**
   * Update plugin props
   */
  updateProps(props: Record<string, any>): void {
    const message: PluginPropsUpdateMessage = {
      type: 'props_update',
      pluginId: this.pluginId,
      props,
    };
    this.sendMessage(message);
  }

  /**
   * Register a custom API handler
   */
  registerHandler(method: string, handler: ApiHandler): void {
    this.apiHandlers.set(method, handler);
  }

  /**
   * Subscribe to events from the plugin
   */
  subscribeToPluginEvents(
    eventType: string,
    handler: (payload: any) => void,
  ): () => void {
    if (!this.eventSubscriptions.has(eventType)) {
      this.eventSubscriptions.set(eventType, new Set());
    }
    this.eventSubscriptions.get(eventType)!.add(handler);

    return () => {
      this.eventSubscriptions.get(eventType)?.delete(handler);
    };
  }

  /**
   * Destroy the bridge
   */
  destroy(): void {
    this.destroyed = true;
    this.ready = false;
    for (const hostSpanId of this.pluginSpans.values()) {
      endFrontendSpan(hostSpanId, 'error', { reason: 'bridge destroyed' });
    }
    this.pluginSpans.clear();
    this.apiHandlers.clear();
    this.eventSubscriptions.clear();
    this.pendingResponses.clear();
    this.savedPluginState = null;
    window.removeEventListener('message', this.handleMessage);
  }

  // ── Wave 18c: Hot Reload DX ───────────────────────────────

  /**
   * Notify the plugin iframe that a hot reload is starting.
   * This triggers the plugin-side overlay and state save.
   */
  notifyHotReloadStart(pluginName?: string): void {
    this.sendMessage({
      type: 'hot_reload_start' as any,
      pluginId: this.pluginId,
      payload: { pluginName },
    } as any);
  }

  /**
   * Notify the plugin iframe that the hot reload succeeded.
   */
  notifyHotReloadSuccess(): void {
    this.sendMessage({
      type: 'hot_reload_success' as any,
      pluginId: this.pluginId,
    } as any);

    // If we have saved state, send it back for restoration
    if (this.savedPluginState) {
      this.sendMessage({
        type: 'hot_reload_state_restore' as any,
        pluginId: this.pluginId,
        payload: this.savedPluginState,
      } as any);
      this.savedPluginState = null;
    }
  }

  /**
   * Notify the plugin iframe that the hot reload failed.
   */
  notifyHotReloadError(error: string, stack?: string): void {
    this.sendMessage({
      type: 'hot_reload_error' as any,
      pluginId: this.pluginId,
      payload: { error, stack },
    } as any);
  }

  /**
   * Request the plugin to save its state (before reload).
   */
  requestStateSave(): void {
    this.sendMessage({
      type: 'hot_reload_state_save' as any,
      pluginId: this.pluginId,
    } as any);
  }

  /**
   * Send a toast notification to the plugin iframe.
   */
  sendPluginToast(variant: string, message: string, duration?: number): void {
    this.sendMessage({
      type: 'plugin_toast' as any,
      pluginId: this.pluginId,
      variant,
      message,
      duration,
    } as any);
  }

  /**
   * Get saved plugin state (for inspection).
   */
  getSavedState(): Record<string, unknown> | null {
    return this.savedPluginState;
  }

  /**
   * Wave 16: Update observability context forwarded to plugin iframe.
   * Call this whenever trace/span/phase changes.
   */
  updateObservabilityContext(ctx: {
    traceId?: string;
    spanId?: string;
    correlationId?: string;
    currentPhase?: CognitivePhase;
  }): void {
    this.observabilityContext = { ...this.observabilityContext, ...ctx };
    if (this.ready) {
      this.sendMessage({
        type: 'observability_context' as any,
        pluginId: this.pluginId,
        ...this.observabilityContext,
      });
    }
  }

  private setupMessageListener(): void {
    window.addEventListener('message', this.handleMessage);
  }

  private handleMessage = (event: MessageEvent): void => {
    if (this.destroyed) return;

    if (event.source !== this.iframe.contentWindow) {
      return;
    }

    // Validate origin (use * for same-origin iframes)
    if (this.allowedOrigin !== '*' && event.origin !== this.allowedOrigin) {
      return;
    }

    const data = event.data;
    if (!isValidPluginMessage(data)) return;
    if (data.pluginId !== this.pluginId) return;

    switch (data.type) {
      case 'ready':
        this.handleReady();
        break;
      case 'api_call':
        this.handleApiCall(data as PluginApiCallMessage);
        break;
      case 'resize':
        if (this.onResize && 'width' in data && 'height' in data) {
          this.onResize(data.width as number, data.height as number);
        }
        break;
      case 'error':
        if (this.onError && 'error' in data) {
          this.onError(data.error as string);
        }
        break;
      // Wave 16: Handle plugin-emitted spans
      case 'plugin_span_start': {
        const attrs = (data as Record<string, unknown>);
        const parentSpanId = attrs.parentSpanId ? this.pluginSpans.get(String(attrs.parentSpanId)) : undefined;
        const hostSpanId = startFrontendSpan(
          'plugin_action',
          (attrs.operationName as string) ?? 'plugin_span',
          { pluginId: this.pluginId, ...((attrs.attributes as Record<string, unknown>) ?? {}) },
          parentSpanId,
        );
        if (attrs.spanId) this.pluginSpans.set(String(attrs.spanId), hostSpanId);
        // Wave 17: Forward span to DevTools if enabled
        if (this.config.devtools && this.config.onPluginSpan) {
          this.config.onPluginSpan({
            spanId: (attrs.spanId as string) ?? '',
            operationName: (attrs.operationName as string) ?? 'plugin_span',
            status: 'in_progress',
            attributes: (attrs.attributes as Record<string, unknown>) ?? {},
          });
        }
        break;
      }
      case 'plugin_span_end': {
        const attrs = (data as Record<string, unknown>);
        const hostSpanId = attrs.spanId ? this.pluginSpans.get(String(attrs.spanId)) : undefined;
        if (hostSpanId) {
          this.pluginSpans.delete(String(attrs.spanId));
          endFrontendSpan(hostSpanId, (attrs.status as 'ok' | 'error') ?? 'ok');
        }
        // Wave 17: Forward span completion to DevTools if enabled
        if (this.config.devtools && this.config.onPluginSpan) {
          this.config.onPluginSpan({
            spanId: (attrs.spanId as string) ?? '',
            operationName: (attrs.operationName as string) ?? 'plugin_span',
            status: (attrs.status as string) ?? 'ok',
            durationMs: (attrs.durationMs as number) ?? undefined,
            attributes: (attrs.attributes as Record<string, unknown>) ?? {},
          });
        }
        break;
      }
      // Wave 17: Handle plugin-emitted logs
      case 'plugin_log': {
        if (this.config.devtools && this.config.onPluginLog) {
          const attrs = (data as Record<string, unknown>);
          this.config.onPluginLog({
            level: (attrs.level as string) ?? 'info',
            message: (attrs.message as string) ?? '',
            timestamp: (attrs.timestamp as string) ?? new Date().toISOString(),
            attributes: (attrs.attributes as Record<string, unknown>) ?? {},
          });
        }
        break;
      }
      // Wave 17: Handle plugin-emitted errors
      case 'plugin_error': {
        if (this.config.devtools && this.config.onPluginError) {
          const attrs = (data as Record<string, unknown>);
          this.config.onPluginError({
            message: (attrs.message as string) ?? 'Unknown plugin error',
            category: (attrs.category as string) ?? undefined,
            code: (attrs.code as string) ?? undefined,
            timestamp: (attrs.timestamp as string) ?? new Date().toISOString(),
          });
        }
        // Also forward to generic error handler
        if (this.onError && 'message' in data) {
          this.onError(`[Plugin Error] ${(data as Record<string, unknown>).message}`);
        }
        break;
      }

      // Wave 18c: Handle plugin state snapshot (saved before reload)
      case 'hot_reload_state_snapshot': {
        const attrs = data as Record<string, unknown>;
        const payload = attrs.payload as Record<string, unknown> | undefined;
        if (payload) {
          this.savedPluginState = payload;
          this.config.onStateSnapshot?.(payload);
        }
        break;
      }

      // Wave 18c: Handle plugin reload retry request
      case 'hot_reload_retry': {
        this.config.onReloadRetry?.();
        break;
      }

      // Wave 18c: Handle plugin DevTools open request
      case 'open_plugin_devtools': {
        this.config.onOpenDevTools?.();
        break;
      }
    }
  };

  private handleReady(): void {
    this.ready = true;
    this.onReady?.();
    // Wave 16: Forward current observability context on ready
    if (this.observabilityContext.traceId || this.observabilityContext.spanId) {
      this.updateObservabilityContext(this.observabilityContext);
    }
  }

  private async handleApiCall(message: PluginApiCallMessage): Promise<void> {
    const { requestId, method, args } = message;

    try {
      const handler = this.apiHandlers.get(method);
      if (!handler) {
        throw new Error(`Unknown API method: ${method}`);
      }

      const result = await handler(args);

      this.sendMessage({
        type: 'api_response',
        pluginId: this.pluginId,
        requestId,
        result,
      });
    } catch (error) {
      this.sendMessage({
        type: 'api_response',
        pluginId: this.pluginId,
        requestId,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  private registerDefaultHandlers(): void {
    const assertSafeApiPath = (path: unknown): string => {
      if (typeof path !== 'string' || !path.trim()) {
        throw new Error('Invalid API path');
      }

      const normalized = path.trim();
      if (/^https?:\/\//i.test(normalized)) {
        throw new Error('Absolute URLs are not allowed in plugin bridge API calls');
      }
      if (normalized.includes('..')) {
        throw new Error('Path traversal is not allowed in plugin bridge API calls');
      }
      if (!normalized.startsWith('/')) {
        throw new Error('API path must be absolute (start with "/")');
      }

      return normalized;
    };

    // HTTP API handlers
    this.registerHandler('api.get', async ([path]) => {
      const safePath = assertSafeApiPath(path);
      const res = await client.get(safePath);
      return res.data;
    });

    this.registerHandler('api.post', async ([path, data]) => {
      const safePath = assertSafeApiPath(path);
      const res = await client.post(safePath, data);
      return res.data;
    });

    this.registerHandler('api.put', async ([path, data]) => {
      const safePath = assertSafeApiPath(path);
      const res = await client.put(safePath, data);
      return res.data;
    });

    this.registerHandler('api.delete', async ([path]) => {
      const safePath = assertSafeApiPath(path);
      const res = await client.delete(safePath);
      return res.data;
    });

    // Config handlers
    this.registerHandler('config.get', async () => {
      const settings = await getPluginSettings(this.pluginId);
      return settings.values;
    });

    this.registerHandler('config.save', async ([config]) => {
      await updatePluginSettings(this.pluginId, config);
    });

    // Toast handlers — wired to real notification system via pluginToastBridge
    this.registerHandler('toast.success', async ([message]) => {
      pluginToastBridge.success(message);
    });

    this.registerHandler('toast.error', async ([message]) => {
      pluginToastBridge.error(message);
    });

    this.registerHandler('toast.info', async ([message]) => {
      pluginToastBridge.info(message);
    });

    // Navigation handlers
    this.registerHandler('navigate', async ([path]) => {
      window.history.pushState({}, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    });

    this.registerHandler('openModal', async ([modalId, props]) => {
      pluginModalBridge.open(this.pluginId, modalId, props);
    });

    this.registerHandler('closeModal', async () => {
      pluginModalBridge.close();
    });

    // Storage handlers
    this.registerHandler('storage.get', async ([key]) => {
      const storageKey = `plugin:${this.pluginId}:${key}`;
      const value = localStorage.getItem(storageKey);
      return value ? JSON.parse(value) : null;
    });

    this.registerHandler('storage.set', async ([key, value]) => {
      const storageKey = `plugin:${this.pluginId}:${key}`;
      localStorage.setItem(storageKey, JSON.stringify(value));
    });

    this.registerHandler('storage.delete', async ([key]) => {
      const storageKey = `plugin:${this.pluginId}:${key}`;
      localStorage.removeItem(storageKey);
    });

    // Event handlers
    this.registerHandler('events.subscribe', async ([eventType]) => {
      // Subscription is handled via postMessage back to plugin
      return { subscribed: true, eventType };
    });

    this.registerHandler('events.publish', async ([eventType, payload]) => {
      // Dispatch custom event for other subscribers
      window.dispatchEvent(
        new CustomEvent(`plugin:${this.pluginId}:${eventType}`, { detail: payload }),
      );
      // Also forward into the unified plugin event bus so iframe-rendered
      // plugins (this.pluginId) can be heard by the same `on(subscriber,
      // sourcePluginId, eventName, handler)` subscribers that directly-
      // rendered plugins already reach via ctx.events.publish (host-ui-api.ts).
      // Previously iframe plugins could only be heard via the raw window
      // CustomEvent above, which requires a listener to already know this
      // plugin's id — the unified bus supports `sourcePluginId: '*'`.
      usePluginEventBusStore.getState().emit(this.pluginId, eventType, payload);
    });
  }
}

/**
 * Create a new plugin bridge
 */
export function createPluginBridge(config: PluginBridgeConfig): PluginBridge {
  return new PluginBridge(config);
}
